<script>
import Layout from '@layouts/login.vue'
import { authMethods } from '@state/helpers'
import baseMixin from '@mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import TheCopyright from '@components/general/the-copyright.vue'
import ServiceStatusAlert from '@/components/app/service-status-alert.vue'

export default {
  name: 'LoginView',
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: this.compMetaDescription }],
    }
  },
  components: { Layout, TheCopyright, ServiceStatusAlert },
  mixins: [baseMixin],
  data() {
    return {
      authError: null,
      valid: false,
      username: '',
      rules: {
        required: (v) => !!v || this.$t('input.inputRequiredValidationText'),
        email: (v) =>
          /.+@.+/.test(v) || this.$t('input.emailRequiredValidationText'),
      },
      password: '',
      resetForm: {
        valid: false,
        email: '',
      },
      showForgotPassword: false,
      showPasswordInput: false,
      resetSuccess: false,
      carousel: 0,
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'auth/isLoadingAuth',
      serviceStatus: 'serviceStatus',
    }),
    isImpersonate() {
      return this.$route?.name === 'impersonateLogin'
    },
    computeTitle() {
      if (this.isImpersonate) return this.$t('login.impersonateFormTitle')
      return !this.showForgotPassword
        ? this.$t('login.loginFormTitle')
        : this.$t('login.forgotFormTitle')
    },
    impersonateContactId() {
      if (!this.isImpersonate) return null
      if (this.$route.params.contactId) {
        const contactId = parseInt(this.$route.params.contactId)
        if (contactId && contactId > 0) return contactId
      }
      return null
    },
    computeDesc() {
      if (this.isImpersonate)
        return this.$t('login.impersonateFormDesc', {
          companyName: this.$t('app.companyName'),
        })
      return !this.showForgotPassword
        ? this.$t('login.loginFormSubtitle')
        : this.$t('login.forgotFormSubtitle')
    },
    compMetaDescription() {
      return `Log in to ${this.$t('app.appName')}`
    },
    hasError() {
      return this.authError
    },
  },
  watch: {
    async isUserLoggedIn() {
      if (this.isUserLoggedIn) {
        if (this.isImpersonate) await this.handleAlreadyLoggedInImpersonate()

        this.$router.push('/')
      }
    },
  },
  async created() {
    await this.handleAlreadyLoggedInImpersonate()
  },
  methods: {
    ...mapActions({
      setLocale: 'setLocale',
      msalLogIn: 'auth/msalLogIn',
      msalRefreshToken: 'auth/msalRefreshToken',
      msalAlreadyLoggedInRedirect: 'auth/msalAlreadyLoggedInRedirect',
      clearImpersonateId: 'auth/clearImpersonateId',
    }),
    ...authMethods,
    async handleLogin() {
      if (!this.$refs.form.validate()) return
      await this.logIn({
        username: this.username,
        password: this.password,
      }).then((result) => {
        if (result.isSuccess)
          this.$router.push(this.$route.query.redirectFrom || { name: 'home' })
        else {
          this.authError = {
            message: result.message,
          }
        }
      })
    },
    async handleResetPassword() {
      this.clearError()
      if (!this.$refs.resetPasswordForm.validate()) {
        return false
      }

      await this.resetPasswordAsync({ email: this.resetForm.email }).then(
        (result) => {
          if (!result.isSuccess) {
            this.authError = {
              message: result.message,
            }
          }
          this.resetSuccess = result.isSuccess
        }
      )

      return false
    },
    async handleAlreadyLoggedInImpersonate() {
      // If on impersonate login page & already logged in, set new impersonate Id
      // and login
      if (this.impersonateContactId) {
        const result = await this.msalRefreshToken()
        if (result.isSuccess) {
          return await this.msalAlreadyLoggedInRedirect(
            this.impersonateContactId
          )
        }
      }
    },
    async loginWithMsal() {
      await this.msalLogIn(parseInt(this.$route.params.contactId)).catch(
        (error) => {
          this.authError = error
        }
      )
    },
    toggleResetForm() {
      this.clearError()
      this.showForgotPassword = !this.showForgotPassword
    },
    clearError() {
      this.resetSuccess = false
      this.authError = null
    },
    compCarouselHeight() {
      return `calc(100vh - ${this.heightOfDevBar}px)`
    },
  },
}
</script>

<template>
  <Layout no-gutters :no-fill-height="isSmallOrLower">
    <v-row no-gutters>
      <v-col
        sm="12"
        md="6"
        lg="5"
        xl="4"
        class="service-status-alert__container"
      >
        <ServiceStatusAlert />
      </v-col>
      <v-col
        sm="12"
        md="6"
        lg="5"
        xl="4"
        class="d-flex flex-column align-center"
      >
        <v-card
          elevation="0"
          :class="[
            !serviceStatus ? 'pt-5' : 'py-10',
            'transparent ma-auto w-full d-flex justify-center flex-column',
          ]"
          :min-width="isSmallOrLower ? '100%' : 480"
          height="100%"
          :min-height="isSmallOrLower ? '100%' : ''"
        >
          <div :class="[!serviceStatus ? '' : 'mt-12', 'ml-4']">
            <BaseLogo :width="isSmallOrLower ? '80px' : '120px'" type="app" />
          </div>
          <v-card-title>
            <h1
              :class="['login-form-title', isMediumOrLower ? 'sm' : '']"
              v-text="computeTitle"
            ></h1>
          </v-card-title>
          <v-card-subtitle class="mt-1">
            <h4 class="subtitle-2" v-text="computeDesc"></h4>
          </v-card-subtitle>

          <v-card-text>
            <v-alert
              v-if="hasError"
              type="error"
              text
              class="mb-6 body-2"
              data-cy="auth-error-alert"
            >
              {{ authError.message }}
            </v-alert>

            <v-alert
              v-if="resetSuccess && !isLoading"
              text
              type="info"
              class="mb-6 body-2"
            >
              {{ $t('login.passwordResetSuccessAlert') }}
            </v-alert>

            <v-form
              v-if="isImpersonate"
              id="impersonate-contact-login-form"
              ref="form"
              v-model="valid"
              lazy-validation
              @keyup.native.enter="valid && loginWithMsal()"
            >
              <v-btn
                v-if="impersonateContactId"
                id="login-submit-btn"
                x-large
                color="blue"
                depressed
                :disabled="!valid"
                :loading="isLoading"
                block
                class="rounded-lg my-6 white--text login-form-submit-btn"
                @click="loginWithMsal"
              >
                <BaseIcon left name="mdi-microsoft"></BaseIcon>
                {{ $t('login.loginBtn') }}
              </v-btn>
              <v-alert
                v-else
                id="impersonate-contact-login-form_invalid-id-alert"
                type="error"
                text
                class="subtitle-2"
              >
                {{ $t('login.impersonateInvalidContactIdAlertText') }}
              </v-alert>
            </v-form>

            <!-- Reset Password Form -->
            <v-form
              v-else-if="showForgotPassword"
              id="resetPasswordForm"
              ref="resetPasswordForm"
              v-model="resetForm.valid"
              lazy-validation
            >
              <v-text-field
                id="resetEmail"
                v-model="resetForm.email"
                :dense="isSmallOrLower"
                name="username"
                :label="$t('input.emailLabel')"
                type="email"
                aria-autocomplete="username"
                autocomplete="username"
                color="indigo"
                class="my-2 font-weight-medium"
                outlined
                required
                :rules="[rules.required, rules.email]"
              ></v-text-field>
              <div class="mt-6">
                <v-btn
                  id="reset-password-submit-btn"
                  x-large
                  color="blue"
                  depressed
                  block
                  :disabled="!resetForm.valid"
                  :loading="isLoading"
                  class="rounded-lg mb-6 white--text reset-password-submit-btn"
                  @click="handleResetPassword"
                >
                  {{ $t('login.forgotFormSubmitBtn') }}
                </v-btn>

                <v-btn
                  id="return-to-login-link"
                  color="blue"
                  text
                  large
                  class="d-inline-block rounded-lg mt-10"
                  @click="toggleResetForm"
                >
                  <v-icon class="mr-2" color="blue"> mdi-chevron-left </v-icon>
                  {{ $t('login.forgotFormReturnBtn') }}
                </v-btn>
              </div>
            </v-form>

            <!-- Login Form -->
            <v-form
              v-else
              ref="form"
              v-model="valid"
              lazy-validation
              @keyup.native.enter="valid && handleLogin()"
            >
              <v-text-field
                id="login-form-username"
                v-model="username"
                name="username"
                type="email"
                :label="$t('input.emailLabel')"
                aria-autocomplete="username"
                autocomplete="username"
                :dense="isSmallOrLower"
                color="indigo"
                class="my-2 font-weight-medium"
                outlined
                required
                :rules="[rules.required, rules.email]"
              ></v-text-field>

              <v-text-field
                id="login-form-password"
                v-model="password"
                name="password"
                :type="showPasswordInput ? 'text' : 'password'"
                :label="$t('input.passwordLabel')"
                aria-autocomplete="new-password"
                autocomplete="new-password"
                :append-icon="showPasswordInput ? 'mdi-eye-off' : 'mdi-eye'"
                color="indigo"
                class="my-2 font-weight-medium"
                outlined
                :dense="isSmallOrLower"
                required
                :rules="[rules.required]"
                @click:append="showPasswordInput = !showPasswordInput"
              ></v-text-field>
              <div
                :class="[isSmallOrLower ? '' : 'mb-6 mt-2', 'ml-2 text-right']"
              >
                <v-btn
                  id="forgot-password-link"
                  color="blue"
                  text
                  large
                  class="rounded-lg"
                  @click="toggleResetForm"
                >
                  {{ $t('login.forgotPasswordLink') }}
                </v-btn>
              </div>
              <v-btn
                id="login-submit-btn"
                data-cy="login-btn"
                x-large
                color="blue"
                depressed
                :disabled="!valid"
                :loading="isLoading"
                block
                class="rounded-lg my-6 white--text login-form-submit-btn"
                @click="handleLogin"
              >
                {{ $t('login.loginBtn') }}
              </v-btn>
            </v-form>

            <TheCopyright />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        v-if="!isSmallOrLower"
        sm="12"
        md="6"
        lg="7"
        xl="8"
        class="d-flex flex-column align-center overflow-hidden"
      >
        <v-sheet
          color="blue"
          class="slide-1"
          width="100%"
          :height="compCarouselHeight()"
        >
          <div class="shapes-container">
            <div
              class="animation-shape shape-ring animation--rotating-diagonal"
            >
              <div></div>
            </div>
            <div class="animation-shape shape-circle animation--clockwise">
              <div></div>
            </div>
            <div
              class="animation-shape shape-triangle animation--anti-clockwise"
            >
              <div class="animation--rotating"></div>
            </div>
            <div
              class="animation-shape shape-diamond animation--anti-clockwise"
            >
              <div class="animation--rotating"></div>
            </div>

            <div class="static-shape shape-ring-1"></div>
            <div class="static-shape shape-ring-2"></div>
            <div class="static-shape shape-circle-1"></div>

            <div class="static-shape pattern-dots-1"></div>
            <div class="static-shape pattern-dots-2"></div>
          </div>

          <v-row
            class="fill-height ma-0"
            align="center"
            justify="center"
            style="position: relative; z-index: 10"
          >
            <v-col lg="9" sm="12">
              <div class="ma-10 pa-4">
                <h2
                  class="font-weight-bold display-1 white--text"
                  style="width: fit-content"
                >
                  <div class="r2bheader">
                    <div class="bgHighlight pr-4"></div>
                    <div class="text">
                      {{ $t('app.appName') }}
                    </div>
                  </div>
                  <h3 class="font-weight-light mt-4">
                    by {{ $t('app.companyNameShort') }}
                  </h3>
                </h2>
                <h3
                  class="font-weight-light mt-10 opacity-9 white--text ws-pre-wrap"
                  style="width: 100%"
                  >{{ $t('app.description') }}</h3
                >
                <section class="nav mt-5 d-flex">
                  <p class="my-0">
                    <a :href="$t('app.appleAppStoreLink')">
                      <img
                        style="height: 40px; margin-right: 1em"
                        src="https://anzukmedia.blob.core.windows.net/assets/cl/landing/apple_download.png"
                      />
                    </a>
                  </p>

                  <p class="my-0">
                    <a :href="$t('app.googlePlayStoreLink')">
                      <img
                        style="height: 40px"
                        src="https://anzukmedia.blob.core.windows.net/assets/cl/landing/android_download.png"
                      />
                    </a>
                  </p>
                </section>
              </div>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
    </v-row>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@/styles/_animations.scss';
@import '@/styles/login.scss';
</style>
